import { Link } from 'react-router-dom';
import { NAV } from './Layout';
import { useAuth } from '../auth';

/**
 * Die Startseite als Kachelfeld.
 *
 * Sie liest dieselbe Menüstruktur wie die Seitenleiste — es gibt keine zweite
 * Liste, die man beim nächsten neuen Modul zu pflegen vergisst, und keine
 * Kachel, die auf etwas zeigt, das die Rolle gar nicht darf.
 *
 * Die Zeichen sind bewusst mit der Hand gezeichnet und nicht aus einer
 * Bibliothek geladen: Sie sind ein Dutzend Linien, und eine Abhängigkeit
 * dafür wäre mehr Last als Nutzen — zumal sie sich über eine fremde Adresse
 * laden würde, die es in einem Lager ohne Leitung nicht gibt.
 */

/** Ein Zeichen je Menüpunkt, als Strichzeichnung im Raster 24×24. */
const ZEICHEN: Record<string, string> = {
  '/dashboard': 'M4 17a8 8 0 1 1 16 0M12 17l4.5-5M12 17h.01',
  '/meldungen': 'M12 3.5l9.5 16.5H2.5zM12 10v4M12 17h.01',
  '/chef': 'M3 21h18M6 21V11M11 21V4M16 21v-6M21 21v-9',
  '/bestand': 'M3 8h18v12H3zM3 8l3-4h12l3 4M12 4v4M8 13h8',
  '/wareneingang': 'M12 3v9m0 0l-3.5-3.5M12 12l3.5-3.5M4 15v4a2 2 0 002 2h12a2 2 0 002-2v-4',
  '/scannen': 'M3 6V4h3M21 6V4h-3M3 18v2h3M21 18v2h-3M7 8v8M11 8v8M14 8v8M18 8v8',
  '/kommissionierung': 'M9 5h11M9 12h11M9 19h11M4 5l1.2 1.5L7.5 3.5M4 12l1.2 1.5L7.5 10.5M4 19l1.2 1.5L7.5 17.5',
  '/packtisch': 'M4 8l8-4 8 4v9l-8 4-8-4zM4 8l8 4 8-4M12 12v9',
  '/nachschub': 'M12 20V7m0 0L7.5 11.5M12 7l4.5 4.5M4 4h16',
  '/chargen': 'M12 3l9 4.5-9 4.5-9-4.5zM3 12l9 4.5 9-4.5M3 16.5L12 21l9-4.5',
  '/seriennummern': 'M5 9h14M5 15h14M10 4L8 20M17 4l-2 16',
  '/buchen': 'M4 5h11l5 5v9a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zM14 5v6h6M7 15h9',
  '/bewegungen': 'M4 8h13m0 0l-3.5-3.5M17 8l-3.5 3.5M20 16H7m0 0l3.5-3.5M7 16l3.5 3.5',
  '/durchlaufzeiten': 'M12 4a8 8 0 100 16 8 8 0 100-16M12 8v4.5l3 2',
  '/inventur': 'M8 4h8v3H8zM6 6H5a1 1 0 00-1 1v13a1 1 0 001 1h14a1 1 0 001-1V7a1 1 0 00-1-1h-1M8.5 13l2 2 4-4.5',
  '/artikel': 'M20.5 11.5L12.5 3.5H4v8.5l8 8zM8 8h.01',
  '/warengruppen': 'M3 7a1 1 0 011-1h5l2 2h9a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1z',
  '/kasse': 'M4 7h16l-1.5 11a1 1 0 01-1 .9H6.5a1 1 0 01-1-.9zM9 7V5a3 3 0 016 0v2',
  '/kassenabschluss': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-6 0h.01M12 16h3m-6 0h.01',
  '/partner': 'M9 11a3.5 3.5 0 100-7 3.5 3.5 0 100 7M2.5 20c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5M17 5.5a3 3 0 010 6M18.5 14.5c1.9.7 3 2.2 3 4',
  '/lagerstruktur': 'M3 4h18v16H3zM3 10h18M3 15h18M9 4v16M15 4v16',
  '/etiketten': 'M3 7h18v10H3zM7 11h6M7 14h4M17 10v5',
  '/import': 'M12 16V4m0 0L8 8m4-4l4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3',
  '/bestellungen': 'M3 4h2l2.5 11h10L20 7H6M9 20a1 1 0 100-2 1 1 0 100 2M17 20a1 1 0 100-2 1 1 0 100 2',
  '/rechnungseingang': 'M5 3h9l5 5v13H5zM14 3v5h5M9 13h6M9 16.5h4',
  '/auftraege': 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 15.5h6M9 19h3',
  '/verkaufspreise': 'M15 8.5a3.5 3.5 0 00-3.5-2c-2 0-3.5 1-3.5 2.6 0 3.4 7 2 7 5.4 0 1.7-1.6 2.8-3.6 2.8A3.7 3.7 0 018 15M11.5 4v16',
  '/warenrechnung': 'M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2zM9 8h6M9 12h6M9 16h3',
  '/kassenpersonal': 'M12 11a3.5 3.5 0 100-7 3.5 3.5 0 100 7M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6',
  '/bank': 'M3 9l9-5 9 5M5 9v9M10 9v9M14 9v9M19 9v9M3 21h18',
  '/zahllauf': 'M12 5v14M8.5 8.5h5.5M9.5 12h5M6 20h12',
  '/lagerwert': 'M3 21h18M6 21V11M11 21V4M16 21v-6M21 21v-9',
  '/rollen': 'M12 11a3.5 3.5 0 100-7 3.5 3.5 0 100 7M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6',
  '/bridges': 'M4 7h16M4 12h16M4 17h16M8 4v3M16 17v3',
  '/kalkulation': 'M4 20L20 4M7.5 4a3.5 3.5 0 100 7 3.5 3.5 0 100-7M16.5 13a3.5 3.5 0 100 7 3.5 3.5 0 100-7',
  '/avu': 'M3 7h18v12H3zM3 11h18M7 15h4M12 3v4M8 5l4-2 4 2',
  '/retouren': 'M20 12a8 8 0 11-2.4-5.7M20 3v5h-5',
  '/ladungstraeger': 'M3 16h18M3 20h18M6 16v4M12 16v4M18 16v4M5 5h14v7H5z',
  '/abrechnung': 'M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2zM9 8h6M9 12h6M9 16h3',
  '/offene-posten': 'M12 5v14M8.5 8.5h5.5M9.5 12h5M6 20h12',
  '/buchhaltung': 'M3 9l9-5 9 5M5 9v9M10 9v9M14 9v9M19 9v9M3 21h18',
  '/module': 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  '/benutzer': 'M12 11a3.5 3.5 0 100-7 3.5 3.5 0 100 7M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6',
  '/mailversand': 'M3 6h18v12H3zM3 7l9 6.5L21 7',
  '/protokoll': 'M5 4h14v16H5zM8 8h8M8 12h8M8 16h5',
  '/system': 'M12 9a3 3 0 100 6 3 3 0 100-6M12 2.5v3M12 18.5v3M4.2 7l2.6 1.5M17.2 15.5l2.6 1.5M4.2 17l2.6-1.5M17.2 8.5l2.6-1.5',
  '/betriebspruefung': 'M11 4a6.5 6.5 0 100 13 6.5 6.5 0 100-13M16 16l4.5 4.5M8.5 10.5h5',
  '/aufbewahrung': 'M3 5h18v4H3zM5 9v11h14V9M10 13h4',
  '/verfahrensdokumentation': 'M4 4h7a2.5 2.5 0 012.5 2.5V20a2 2 0 00-2-2H4zM20 4h-7a2.5 2.5 0 00-2.5 2.5V20a2 2 0 012-2h7.5z',
};

/** Für Punkte, die noch nicht gebaut sind, und als Rückfall. */
const PLATZHALTER = 'M4 5h16v14H4zM8 9h8M8 13h5';

function Zeichen({ pfad }: { pfad: string | undefined }) {
  return (
    <svg
      className="kachel-zeichen"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={pfad ?? PLATZHALTER} />
    </svg>
  );
}

/** Ein Satz je Menüpunkt — die Kachel soll erklären, nicht nur beschriften. */
const ERKLAERUNG: Record<string, string> = {
  '/dashboard': 'Bestand, Auslastung und was heute bewegt wurde',
  '/meldungen': 'Was knapp wird — sortiert nach Reichweite',
  '/chef': 'Umsatz, Auslastung und Entwicklung über Monate',
  '/bestand': 'Was liegt wo, gefiltert nach Artikel und Platz',
  '/wareneingang': 'Avise anlegen und eintreffende Ware einbuchen',
  '/scannen': 'Barcode lesen und direkt zum Artikel springen',
  '/kommissionierung': 'Offene Pickaufgaben und Wellen',
  '/packtisch': 'Verpacken, Zeit nehmen, Lieferschein drucken',
  '/nachschub': 'Kommissionierplätze auffüllen',
  '/chargen': 'Wo liegt eine Charge, und an wen ging sie',
  '/seriennummern': 'Einzelne Geräte vom Eingang bis zum Kunden',
  '/buchen': 'Wareneingang, Umlagerung und Korrektur von Hand',
  '/bewegungen': 'Das Journal — jede Buchung mit Zeit und Person',
  '/durchlaufzeiten': 'Wie lange ein Auftrag braucht, und was er kostet',
  '/inventur': 'Zählen, Differenzen buchen, Abschluss',
  '/artikel': 'Artikelstamm mit Charge, Serie und Gefahrgut',
  '/warengruppen': 'Artikel fachlich ordnen, Auswertung je Gruppe',
  '/kasse': 'Verkauf am Tresen, Bon mit TSE',
  '/kassenabschluss': 'Kassenschnitt, Tagesabschluss, Personal',
  '/partner': 'Kunden, Lieferanten und Auftraggeber',
  '/lagerstruktur': 'Lager, Zonen und Plätze',
  '/etiketten': 'Artikel- und Platzetiketten als PDF',
  '/import': 'Stammdaten aus Tabellen übernehmen',
  '/bestellungen': 'Beim Lieferanten bestellen und nachhalten',
  '/rechnungseingang': 'Eingangsrechnungen, auch als E-Rechnung',
  '/auftraege': 'Angebote und Kundenaufträge bis zum Versand',
  '/warenrechnung': 'Versendete Aufträge berechnen',
  '/verkaufspreise': 'Kundenpreise und Staffeln',
  '/kassenpersonal': 'Wer an der Kasse steht',
  '/bank': 'Kontoauszug einlesen, Posten zuordnen',
  '/zahllauf': 'SEPA-Überweisungen aus Eingangsrechnungen',
  '/lagerwert': 'Warenwert und Rohertrag',
  '/rollen': 'Welche Rolle welchen Bereich sieht',
  '/bridges': 'Vor-Ort-Box für den Offline-Betrieb',
  '/kalkulation': 'Spanne, Aufschlag und Rohertrag je Artikel',
  '/avu': 'Herstellerzuschüsse: verkauft, angefordert, bezahlt',
  '/retouren': 'Rücksendungen prüfen und einbuchen',
  '/ladungstraeger': 'Palettenkonto je Partner',
  '/abrechnung': 'Lagergeld, Tarife und Rechnungen',
  '/offene-posten': 'Fälligkeiten, Zahlungen, Mahnstufen',
  '/buchhaltung': 'DATEV-Stapel, XRechnung und ZUGFeRD',
  '/module': 'Betriebsart und was gebucht ist',
  '/benutzer': 'Konten, Rollen und Zugänge',
  '/mailversand': 'Belege per E-Mail versenden',
  '/protokoll': 'Änderungen, Anmeldungen und Zugriffe',
  '/system': 'Zeitpläne, Hintergrundaufträge, Datensicherung',
  '/betriebspruefung': 'Prüferzugang und Datenträger nach § 147 AO',
  '/aufbewahrung': 'Fristen, Sperren und der Löschlauf',
  '/verfahrensdokumentation': 'Das Dokument, das die GoBD verlangt',
};

export function Kacheln() {
  const { user, can, verborgen, bereiche } = useAuth();
  const isPortal = user?.role === 'auftraggeber';

  return (
    <div className="kachelfeld">
      {NAV.map((gruppe) => {
        // Dieselbe Regel wie in der Seitenleiste. Die Startseite darf nichts
        // zeigen, was das Menü verbirgt.
        const sichtbar = gruppe.items.filter(
          (item) =>
            item.to !== '/' &&
            (!item.to || !verborgen.has(item.to)) &&
            (isPortal
              ? item.portal
              : bereiche.has(gruppe.bereich) && (!item.roles || can(...item.roles))),
        );
        if (sichtbar.length === 0) return null;

        return (
          <section key={gruppe.title} className="kachelgruppe">
            <h2>{gruppe.title}</h2>
            <div className="kacheln">
              {sichtbar.map((item) => {
                const beschriftung = (isPortal && item.portalLabel) || item.label;
                const inhalt = (
                  <>
                    <Zeichen pfad={item.to ? ZEICHEN[item.to] : undefined} />
                    <span className="kachel-titel">{beschriftung}</span>
                    <span className="kachel-text">
                      {item.to ? ERKLAERUNG[item.to] : 'noch nicht verfügbar'}
                    </span>
                  </>
                );

                return item.to ? (
                  <Link key={item.label} to={item.to} className="kachel">
                    {inhalt}
                  </Link>
                ) : (
                  <div key={item.label} className="kachel geplant">
                    {inhalt}
                    <span className="soon">geplant</span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
